<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_SAVINGS_POCKET')"
            :label="$t('Add')"
            size="md"
            class="mt-0"
            @click="goToAddSavingsPockets"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="savingsPocketList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(startDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>
      <template #cell(endDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_SAVINGS_POCKET') && data.item.savingsType !== 'FlexSave'"
              @click="onSelectSavingsPocketItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectSavingsPocketItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>
            <!-- <b-dropdown-item @click="savingsPocketViewHistory(data.item)">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View History') }}</span>
            </b-dropdown-item> -->
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_SAVINGS_POCKET' : 'ACTIVATE_SAVINGS_POCKET')"
              @click="accountSavingsPocketStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="savingsPocketLiquidate(data.item)">
              <feather-icon icon="TrendingDownIcon" />
              <span class="align-middle ml-50">{{ $t('Liquidate') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.savingsType === 'FlexSave'"
              @click="flexibleTopUp(data.item)"
            >
              <feather-icon icon="PlusCircleIcon" />
              <span class="align-middle ml-50">{{ $t('Flexible Top-Up') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- flexible top-up modal -->
    <b-modal
      id="flexible-top-up-modal-lg"
      ref="my-flexible-top-up-modal"
      no-close-on-backdrop
      :ok-title="$t('Post Top-Up')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="$t('Flexible Top-Up')"
      @ok="handleFlexibleTopupSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="flexibleTopUpRules">
        <b-form>
          <app-collapse
            class="business-items mb-4"
            type="margin"
            accordion
          >
            <app-collapse-item
              :title="$t('Flexible Account Summary')"
              class="card-item"
              is-visible
            >
              <b-row>
                <b-col md="4">
                  <InputField
                    id="savings-name"
                    v-model="customer.savingsName"
                    :label="$t('Savings Name')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="flexible-account-number"
                    v-model="customer.accountNumber"
                    :label="$t('Flexible Account Number')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="product-name"
                    v-model="customer.productName"
                    :label="$t('Product Name')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="ledger-balance"
                    v-model="customer.ledgerBalance"
                    :label="$t('Ledger Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="account-status"
                    v-model="customer.accountStatus"
                    :label="$t('Account Status')"
                    type="text"
                    readonly
                  />
                </b-col>
              </b-row>
            </app-collapse-item>
          </app-collapse>
          <h5 class="text-capitalize mb-3">
            {{ $t('Top-Up Details') }}
          </h5>
          <b-row>
            <b-col md="4">
              <InputNumberField
                id="amount"
                v-model="flexibleTopUpData.amount"
                :label="$t('Top up Amount')"
                type="number"
                :error-name="$t('Top up Amount')"
                rules="required|positive"
              />
            </b-col>
            <!-- <b-col md="4">
              <InputField
                id="new-account-number"
                v-model="flexibleTopUpData.newAccountNumber"
                :label="$t('New Flexible Account Number')"
                type="text"
              />
            </b-col>
            <b-col md="4">
              <InputField
                id="instrument-number"
                v-model="flexibleTopUpData.instrumentNumber"
                :label="$t('Instrument Number')"
                type="text"
              />
            </b-col> -->
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- flexible top-up modal -->

    <!-- Liquidation modal -->
    <b-modal
      id="liquidation-modal-lg"
      ref="my-liquidation-modal"
      no-close-on-backdrop
      :ok-title="$t('Liquidate')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="$t('Liquidation')"
      @ok="handleLiquidationSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="liquidationRules">
        <b-form>
          <app-collapse
            class="business-items mb-4"
            type="margin"
            accordion
          >
            <app-collapse-item
              :title="$t('Liquidation Account Summary')"
              class="card-item"
              is-visible
            >
              <b-row>
                <b-col md="4">
                  <InputField
                    id="savings-name"
                    v-model="liquidationFormData.savingsName"
                    :label="$t('Savings Name')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="frequent-account-number"
                    v-model="liquidationFormData.accountNumber"
                    :label="$t('Frequent Account Number')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="product-name"
                    v-model="liquidationFormData.productName"
                    :label="$t('Product Name')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputNumberField
                    id="available-balance"
                    v-model="liquidationFormData.availableBalance"
                    :label="$t('Available Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputNumberField
                    id="withdrawable-balance"
                    v-model="liquidationFormData.withdrawableBalance"
                    :label="$t('Withdrawable Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputNumberField
                    id="ledger-balance"
                    v-model="liquidationFormData.ledgerBalance"
                    :label="$t('Ledger Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <SelectField
                    id="liquidation-type"
                    v-model="liquidationFormData.liquidationType"
                    :label="$t('Liquidation Type')"
                    :reduce="option => option.value"
                    :options="liquidationTypesOptions"
                    item="label"
                    :placeholder="$t('Liquidation Type')"
                    :readonly="isReadonly"
                    :error-name="$t('Liquidation Type')"
                    rules="required"
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="accrued-interest"
                    v-model="liquidationFormData.accruedInterest"
                    :label="$t('Accrued Interest')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="penalty"
                    v-model="liquidationFormData.penaltyAmount"
                    :label="$t('Penalty')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="interest-to-be-paid"
                    v-model="liquidationFormData.interestToBePaid"
                    :label="$t('Interest To Be Paid')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputNumberField
                    id="total-amount-to-widthraw"
                    v-model="liquidationFormData.amountToWidthraw"
                    :label="$t('Total Amount To Widthraw')"
                    type="text"
                    :readonly="liquidationFormData.liquidationType === 'PartLiquidation' ? false : true"
                    @input="handleTotalAmountToWidthraw"
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="account-status"
                    v-model="liquidationFormData.accountStatus"
                    :label="$t('Account Status')"
                    type="text"
                    readonly
                  />
                </b-col>
              </b-row>
            </app-collapse-item>
          </app-collapse>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- Liquidation modal -->

    <!-- modal -->
    <b-modal
      id="savings-pockets-modal-lg"
      ref="my-savings-pockets-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="updateSavingsPocketRules">
        <b-form>
          <!-- <b-row class="match-height"> -->
          <b-row>
            <b-col md="4">
              <SelectField
                id="reasons"
                v-model="savingsPocketFormData.liquidationAccount"
                :label="$t('Fund Source/Liquidation Account')"
                rules="required"
                :error-name="$t('Fund Source/Liquidation Account')"
                :reduce="option => option.value"
                :options="customerAccountOptions"
                item="label"
                :placeholder="$t('Select Fund Source/Liquidation Account')"
                :readonly="isEditable"
              />
            </b-col>
            <b-col md="4">
              <SelectField
                id="savings-pocket-type"
                v-model="savingsPocketFormData.savingsType"
                :label="$t('Savings Pocket Type')"
                rules="required"
                :error-name="$t('Savings Pocket Type')"
                :reduce="option => option.value"
                :options="savingsTypesOptions"
                item="label"
                :placeholder="$t('Select Savings Pocket Type')"
                :readonly="isEditable"
              />
            </b-col>
            <b-col md="4">
              <SelectField
                id="product"
                v-model="savingsPocketFormData.productCode"
                :label="$t('Product')"
                :options="getProductOptions(savingsPocketFormData.savingsType)"
                :error-name="$t('Product')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isEditable"
              />
            </b-col>
          </b-row>
          <b-row v-if="savingsPocketFormData.savingsType === 'FrequentSave'">
            <b-col md="4">
              <InputField
                id="frequent-saving-name"
                v-model="savingsPocketFormData.pocketName"
                :label="$t('Frequent Saving Name')"
                type="text"
                :error-name="$t('Frequent Saving Name')"
                rules="required"
                :readonly="isEditable"
              />
            </b-col>
            <b-col md="4">
              <InputNumberField
                id="frequent-amount-maturity"
                v-model="savingsPocketFormData.amount"
                :label="$t('Frequent Amount at Maturity')"
                type="number"
                :error-name="$t('Frequent Amount at Maturity')"
                rules="required|positive"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="4">
              <SelectField
                id="frequent-saving-frequency"
                v-model="savingsPocketFormData.frequency"
                :label="$t('Frequent Saving Frequency')"
                :options="frequencyOptions"
                :error-name="$t('Frequent Saving Frequency')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isEditable"
              />
            </b-col>
            <b-col md="4">
              <InputField
                id="interest-rate"
                v-model="savingsPocketFormData.interestRate"
                :label="$t('Interest Rate')"
                type="text"
                readonly
              />
            </b-col>
          </b-row>
          <b-row
            v-if="savingsPocketFormData.savingsType === 'FrequentSave' &&
              ['Daily', 'Weekly', 'Monthly'].includes(
                savingsPocketFormData.frequency
              )"
          >
            <b-col
              v-if="savingsPocketFormData.frequency === 'Daily'"
              md="4"
            >
              <InputField
                id="duration"
                v-model="savingsPocketFormData.duration"
                :label="$t('Duration (in days)')"
                type="number"
                :error-name="$t('Duration (in days)')"
                rules="required|positive|min:1"
                :readonly="isReadonly"
                @input="setEstimatedSavingsAmount(savingsPocketFormData.duration, 'Daily')"
              />
            </b-col>
            <b-col
              v-if="savingsPocketFormData.frequency === 'Weekly'"
              md="4"
            >
              <SelectField
                id="duration-weekly"
                v-model="savingsPocketFormData.duration"
                :label="$t('Duration (in weeks)')"
                :options="weeklyOptions"
                :error-name="$t('Duration (in weeks)')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :set-selected="setEstimatedSavingsAmount(savingsPocketFormData.duration, 'Weekly')"
              />
            </b-col>
            <b-col
              v-if="savingsPocketFormData.frequency === 'Monthly'"
              md="4"
            >
              <SelectField
                id="duration-monthly"
                v-model="savingsPocketFormData.duration"
                :label="$t('Duration (in months)')"
                :options="monthlyOptions"
                :error-name="$t('Duration (in months)')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :set-selected="setEstimatedSavingsAmount(savingsPocketFormData.duration, 'Monthly')"
              />
            </b-col>
            <b-col md="4">
              <DatePickerField
                id="start-date"
                v-model="savingsPocketFormData.startDate"
                rules="required"
                :min-date="new Date()"
                :label="$t('Start Date')"
                :error-name="$t('Start Date')"
                :on-change="setEndDate"
                :config="currentFinDatePickerConfig"
                :placeholder="$t('Select Start Date')"
                :disabled="isEditable"
              />
            </b-col>
            <b-col md="4">
              <DatePickerField
                id="end-date"
                v-model="savingsPocketFormData.endDate"
                :rules="`required|date-limit:${currentEndDate}`"
                :label="$t('End Date')"
                :error-name="$t('End Date')"
                :config="endDatePickerConfig"
                :placeholder="$t('Select End Date')"
                :disabled="isReadonly"
              />
            </b-col>
            <b-col md="4">
              <InputNumberField
                id="estimated-savings-amount"
                v-model="savingsPocketFormData.deductionAmount"
                :label="$t('Estimated Savings Amount')"
                type="number"
                :error-name="$t('Estimated Savings Amount')"
                rules="required"
                readonly
              />
            </b-col>
          </b-row>
          <b-row v-if="savingsPocketFormData.savingsType === 'FlexSave'">
            <b-col md="4">
              <InputField
                id="flex-saving-name"
                v-model="savingsPocketFormData.pocketName"
                :label="$t('Flex Saving Name')"
                type="text"
                :error-name="$t('Flex Saving Name')"
                rules="required"
                :readonly="isEditable"
              />
            </b-col>
            <b-col md="4">
              <InputNumberField
                id="flex-savings-amount"
                v-model="savingsPocketFormData.amount"
                :label="$t('Amount to Save')"
                type="number"
                :error-name="$t('Amount to Save')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="4">
              <DatePickerField
                id="flex-start-date"
                v-model="savingsPocketFormData.startDate"
                rules="required"
                :min-date="new Date()"
                :label="$t('Start Date')"
                :error-name="$t('Start Date')"
                :config="currentFinDatePickerConfig"
                :placeholder="$t('Select Start Date')"
                :disabled="isReadonly"
              />
            </b-col>
          </b-row>
          <b-row v-if="savingsPocketFormData.savingsType === 'SmartSave'">
            <b-col md="4">
              <InputField
                id="flex-saving-name"
                v-model="savingsPocketFormData.pocketName"
                :label="$t('Flex Saving Name')"
                type="text"
                :error-name="$t('Flex Saving Name')"
                rules="required"
                :readonly="isEditable"
              />
            </b-col>
            <b-col md="4">
              <InputNumberField
                id="flex-savings-amount"
                v-model="savingsPocketFormData.percentage"
                :label="$t('Percentage of spent amount to be saved')"
                type="number"
                :error-name="$t('Percentage of spent amount to be saved')"
                rules="required|positive|min-value|max-value:100"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="4">
              <DatePickerField
                id="auto-start-date"
                v-model="savingsPocketFormData.startDate"
                rules="required"
                :min-date="new Date()"
                :label="$t('Start Date')"
                :error-name="$t('Start Date')"
                :config="currentFinDatePickerConfig"
                :placeholder="$t('Select Start Date')"
                :disabled="isEditable"
              />
            </b-col>
          </b-row>

          <!-- </b-row> -->
          <FullScreenSpinner :show="showSpinner" />
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- memo modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchSavingsPocket"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <b-col md="6">
          <InputField
            id="initiated-by"
            v-model="filter.initiatedBy"
            :label="$t('Initiated By')"
            :placeholder="$t('Enter Initiated By')"
            type="email"
          />
        </b-col>

        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option => option.value"
            :options="memoStatus"
            item="label"
            :placeholder="$t('Select Status')"
          />
        </b-col>

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BForm, BBadge,
  BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { required, positive } from '@validations'
import { ValidationObserver } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
// import { memoAccountActionTypes, memoAccountReasons } from '@/helpers/data'
import getStatusColor from '@/helpers/getStatusColor'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import { accountPermissions } from '@/helpers/permissions'
import Button from '../../../../components/button/Button.vue'
import { toKobo, toBalance } from '@/helpers/formatAmount'
import { toDateWithYear } from '@/helpers/formatDate'
import DatePickerField from '../../../../forms/DatePickerField.vue'

export default {
  components: {
    BCard,
    BForm,
    BRow,
    BCol,
    Button,
    BBadge,
    BDropdown,
    BDropdownItem,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    InputNumberField,
    FullScreenSpinner,
    DatePickerField,
    ValidationObserver,
    AppCollapse,
    AppCollapseItem,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      required,
      positive,
      getStatusColor,
      modalTitle: '',
      isEditable: false,
      isReadonly: false,
      toBalance,
      toDateWithYear,
      customer: {
        savingsName: '',
        accountNumber: '',
        productName: '',
        accountStatus: '',
        ledgerBalance: '',
      },
      flexibleTopUpData: {
        flexSaveID: '',
        amount: 0,
        newAccountNumber: '',
        instrumentNumber: '',
      },
      liquidationFormData: {
        savingsName: '',
        accountNumber: '',
        availableBalance: 0,
        withdrawableBalance: 0,
        ledgerBalance: 0,
        productName: '',
        liquidationType: '',
        liquidationAccount: '',
        penaltyAmount: '',
        interestToBePaid: '',
        accruedInterest: '',
        amountToWidthraw: 0,
        accountStatus: '',
        id: '',
      },
      liquidationTypesOptions: [
        { value: 'FullLiquidation', label: this.$t('Full Liquidation') },
        { value: 'PartLiquidation', label: this.$t('Part Liquidation') },
      ],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'savingsType', sortable: true, label: 'Savings Type' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'pocketName', sortable: true, label: 'Savings Pocket Name' },
        { key: 'accountNumber', sortable: true, label: 'Savings Pocket Account Number' },
        { key: 'liquidationAccount', sortable: true, label: 'Source Account Number' },
        // { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'product', sortable: true, label: 'Product' },
        // { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'amount', sortable: true, label: 'Target Amount' },
        { key: 'startDate', sortable: true, label: 'Start Date' },
        { key: 'endDate', sortable: true, label: 'Maturity Date' },
        { key: 'status', sortable: true, label: 'Status' },
        // { key: 'status', sortable: true, label: 'Creation Date' },
        { key: 'actions', label: 'Actions' },
      ],
      savingsPocketList: [],
      savingsPocketFormData: {
        id: '',
        pocketName: '',
        liquidationAccount: '',
        savingsAccount: '',
        accountName: '',
        customerID: '',
        amount: 0,
        percentage: 0,
        deductionAmount: 0,
        totalDeposit: 0,
        productCode: '',
        productName: '',
        product: '',
        frequency: '',
        frequencyInDays: null,
        startDate: '',
        endDate: '',
        interestRate: 0,
        status: '',
        accruedInterest: 0,
        savingsType: '',
        duration: '',
      },

      savingsRate: '',
      bankDaysInYear: '',

      currentEndDate: '',
      fdProductOptions: [],
      casaProductOptions: [],
      customerAccountOptions: [],
      savingsTypesOptions: [
        {
          value: 'FrequentSave',
          label: this.$t('Frequent Savings'),
        },
        {
          value: 'FlexSave',
          label: this.$t('Flex Savings'),
        },
        {
          value: 'SmartSave',
          label: this.$t('AutoSave Savings'),
        },
      ],
      currentFinDatePickerConfig: {
        dateFormat: 'Y-m-d',
        minDate: '',
        defaultDate: '',
      },
      endDatePickerConfig: {
        dateFormat: 'Y-m-d',
        ariaDateFormat: 'Y-m-d',
      },

      filter: {
        // savingsType: 'FrenquentSave',
        accountNumber: null,
        status: null,
        initiatedBy: '',
      },
      memoStatus: [
        { label: this.$t('Active'), value: 'Active' },
        { label: this.$t('InActive'), value: 'InActive' },
      ],
      currentSavingsType: '',
      showSpinner: false,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    weeklyOptions() {
      const totalWeeks = 52
      return Array.from({ length: totalWeeks }, (_, i) => ({
        value: String(i + 1),
        label: `Week ${i + 1}`,
      }))
    },
    // preferredMonthOptions() {
    //   return Array.from({ length: 31 }, (_, i) => ({
    //     value: String(i + 1),
    //     label: this.$t(String(i + 1)),
    //   }))
    // },
    monthlyOptions() {
      return Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1),
        label: this.$t(String(i + 1)),
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchSavingsPocket()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchSavingsPocket()
    },
  },
  mounted() {
    this.fetchSavingsPocket()
    this.fetchCASAProducts()
    this.fetchFDProducts()
    this.fetchCustomerAccount()
  },
  methods: {
    ...mapActions({
      getSavingsPocket: 'account/getSavingsPocketAction',
      // updateMemoStatus: 'account/updateStatus',
      updateSavingsPocketAccounts: 'account/updateSavingsPocketAccounts',
      updateSavingsPocketStatus: 'account/updateSavingsPocketStatus',
      flexibleTopupAccounts: 'account/flexibleTopupAccounts',
      getLiquidationAccountDetail: 'account/getLiquidationAccountDetail',
      // getUserBalance: 'account/getCASABalanceAction',

      getProducts: 'account/getProducts',
      getSavingsRate: 'eChannels/getSavingsRateDetails',
      searchCustomerAccount: 'account/searchCustomerAccountAction',
      performLiquidation: 'account/performLiquidationAction',
    }),
    goToAddSavingsPockets() {
      this.$router.push('add-savings-pockets')
    },
    async fetchSavingsPocket() {
      try {
        this.isBusy = true
        const response = await this.getSavingsPocket({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: 'VIEW_SAVINGS_POCKET',
        })
        const { data } = response
        if (data.data !== null) {
          this.isBusy = false
          this.savingsPocketList = data.data.map(item => ({
            ...item,
            amount: item.amount ? toBalance(item.amount) : 0,
            startDate: item.commencementDate || null,
            endDate: item.maturityDate || null,
            deductionAmount: item.deductionAmount ? toBalance(item.deductionAmount) : 0,
            duration: item.duration.toString(),
          }))
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    isFrequentSave(savingsType) {
      return savingsType === 'FrequentSave'
    },
    getProductOptions(savingsType) {
      return this.isFrequentSave(savingsType)
        ? this.fdProductOptions
        : this.casaProductOptions
    },
    setEndDate(dateStr) {
      const selectedDate = Array.isArray(dateStr) ? dateStr[0] : dateStr
      const startDate = new Date(selectedDate)
      const durations = Number(this.savingsPocketFormData.duration) || 0
      const { frequency } = this.savingsPocketFormData
      if (!durations) {
        this.savingsPocketFormData.endDate = ''
        return
      }
      let totalDays = 0
      switch (frequency) {
        case 'Weekly':
          totalDays = durations * 7 // Convert weeks to days
          break
        case 'Monthly':
          totalDays = durations * 30 // Convert months to days
          break
        case 'Daily':
        default:
          totalDays = durations // Already in days
          break
      }
      // Add total days to start date
      startDate.setDate(startDate.getDate() + Number(totalDays))
      // eslint-disable-next-line prefer-destructuring
      this.savingsPocketFormData.endDate = startDate.toISOString().split('T')[0]
    },
    setEstimatedSavingsAmount(durations, durationType) {
      let totalDuration = 0
      if (durationType === 'Weekly') {
        totalDuration = Number(durations) * 7
      } else if (durationType === 'Monthly') {
        totalDuration = Number(durations) * 30
      } else {
        totalDuration = Number(durations)
      }
      const dailyAmount = (Number(this.savingsPocketFormData.amount) / totalDuration)
      const interestRate = Number(this.savingsRate) / 100
      this.savingsPocketFormData.deductionAmount = (dailyAmount * (interestRate / Number(this.bankDaysInYear))) * ((Number(totalDuration) * (Number(totalDuration) + 1)) / 2)
    },
    async getRateAndBankDaysInYear(savingsType) {
      if (savingsType === 'FrequentSave') {
        try {
          const response = await this.getSavingsRate({ permission: accountPermissions.VIEW_SAVINGS_POCKET })
          const { status, data } = response
          if (status === 200 && data?.data) {
            this.savingsRate = data?.data?.[0]?.config?.[0]?.applicableInterestRate || 0
            this.bankDaysInYear = data?.daysInYear || 0
          }
        } catch (error) {
          this.alertError({ text: error })
        }
      }
    },
    async fetchCustomerAccount() {
      try {
        const response = await this.searchCustomerAccount({
          search: { type: 'Savings', accountNumber: this.savingsPocketFormData.liquidationAccount || '' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVINGS_POCKET,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.customerAccountOptions = data.data.map(item => ({
            label: `${item.accountName} - ${item.accountNumber}`,
            value: item.accountNumber,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getProducts({
          search: { type: 'Savings' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVINGS_POCKET,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.casaProductOptions = data.data.filter(itemValue => itemValue.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchFDProducts() {
      try {
        const response = await this.getProducts({
          search: { type: 'FixedDeposit' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVINGS_POCKET,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.fdProductOptions = data.data.filter(itemValue => itemValue.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.updateSavingsPocketRules.validate().then(async success => {
        if (success) {
          const actionText = this.$t('Are you sure you want to update this Savings Pocket Account?')
          this.confirm(this.updateSavingsPocketAccount, { text: actionText })
        }
      })
    },
    async updateSavingsPocketAccount() {
      try {
        this.showSpinner = true
        this.isLoading = true
        this.isDisabled = true

        let savingsModelItems = {}
        if (this.savingsPocketFormData.savingsType === 'FrequentSave' && ['Monthly', 'Weekly'].includes(this.savingsPocketFormData.frequency)) {
          savingsModelItems = {
            amount: toKobo(this.savingsPocketFormData.amount),
            maturityDate: this.savingsPocketFormData.endDate,
            duration: this.savingsPocketFormData.duration,
            id: this.savingsPocketFormData.id,
          }
        } else if (this.savingsPocketFormData.savingsType === 'FrequentSave' && this.savingsPocketFormData.frequency === 'Daily') {
          savingsModelItems = {
            amount: toKobo(this.savingsPocketFormData.amount),
            duration: this.savingsPocketFormData.duration,
            maturityDate: this.savingsPocketFormData.endDate,
            id: this.savingsPocketFormData.id,
          }
        } else if (this.savingsPocketFormData.savingsType === 'FlexSave') {
          savingsModelItems = {
            amount: toKobo(this.savingsPocketFormData.amount),
            commencementDate: this.savingsPocketFormData.startDate,
            id: this.savingsPocketFormData.id,
          }
        } else {
          savingsModelItems = {
            percentage: this.savingsPocketFormData.percentage,
            id: this.savingsPocketFormData.id,
          }
        }
        const formData = {
          savingsType: this.savingsPocketFormData.savingsType,
          savingsModel: savingsModelItems,
        }
        const response = await this.updateSavingsPocketAccounts({ formData, permission: accountPermissions.MODIFY_SAVINGS_POCKET })

        const { status, data } = response
        if (status === 200 && data) {
          this.isLoading = false
          this.isDisabled = false
          this.showSpinner = false
          this.fetchSavingsPocket()
          this.alertSuccess({ text: this.$t('Your request to Modify Savings Pocket was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
        this.showSpinner = false
      }
    },
    onSelectSavingsPocketItem(items, activity, val) {
      try {
        this.savingsPocketFormData = {
          ...items,
          startDate: items.commencementDate || null,
          endDate: items.maturityDate || null,
        }

        this.currentEndDate = items.maturityDate

        this.savingsPocketFormData.endDate = items.maturityDate
        this.$nextTick(() => {
          const maturityDate = new Date(items.maturityDate)
          maturityDate.setHours(0, 0, 0, 0)

          this.endDatePickerConfig = {
            dateFormat: 'Y-m-d',
            ariaDateFormat: 'Y-m-d',
            disable: [
              date => {
                const checkDate = new Date(date)
                checkDate.setHours(0, 0, 0, 0)

                // Disable dates <= maturityDate
                return checkDate.getTime() <= maturityDate.getTime()
              },
            ],
          }
        })
        this.setEstimatedSavingsAmount(items.duration, items.frequency)
        this.getRateAndBankDaysInYear(items.savingsType)
        this.isEditable = true
        this.isReadonly = val !== 'edit'
        this.modalTitle = val === 'edit' ? this.$t('Edit Savings Pockets') : this.$t('View Savings Pockets')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'savings-pockets-modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    flexibleTopUp(items) {
      this.flexibleTopUpData.flexSaveID = items.id
      this.customer.savingsName = items.pocketName
      this.customer.accountNumber = items.accountNumber
      this.customer.productName = items.productName
      this.customer.accountStatus = items.status
      this.customer.ledgerBalance = items.ledgerBalance
      this.$root.$emit('bv::show::modal', 'flexible-top-up-modal-lg')
    },
    handleFlexibleTopupSubmit(ev) {
      ev.preventDefault()
      this.$refs.flexibleTopUpRules.validate().then(async success => {
        if (success) {
          const actionText = this.$t('Are you sure you want to Top-Up this Flexible Savings Account?')
          this.confirm(this.submitFlexibleTopUp, { text: actionText })
        }
      })
    },
    async submitFlexibleTopUp() {
      try {
        this.showSpinner = true
        this.isLoading = true
        this.isDisabled = true

        const formData = {
          flexSaveID: this.flexibleTopUpData.flexSaveID,
          amount: toKobo(this.flexibleTopUpData.amount),
        }

        const response = await this.flexibleTopupAccounts({ formData, permission: accountPermissions.MODIFY_SAVINGS_POCKET })

        const { status, data } = response
        if (status === 200 && data) {
          this.isLoading = false
          this.isDisabled = false
          this.showSpinner = false
          this.fetchSavingsPocket()
          const text1 = this.$t('Your request to Top-Up')
          const text2 = this.$t('was successful')
          const text3 = this.$t('Account')
          this.alertSuccess({ text: `${text1} (${data.pocketName} - ${data.accountNumber}) ${text3} with ${toBalance(data.topupAmount)} ${text2}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
        this.showSpinner = false
      }
    },

    async savingsPocketLiquidate(items) {
      try {
        const routeNameMap = {
          FrequentSave: 'FrequentSave',
          SmartSave: 'SmartSave',
          Flexsave: 'FlexSave',
        }

        const routeName = routeNameMap[items.savingsType] ?? 'FlexSave'
        this.currentSavingsType = routeName
        this.showSpinner = true
        const response = await this.getLiquidationAccountDetail({ id: items.id, routeName, permission: accountPermissions.VIEW_SAVINGS_POCKET })
        const { data, status } = response
        if (status === 200) {
          this.liquidationFormData = {
            availableBalance: data.data.availableBalance ? toBalance(data.data.availableBalance) : 0,
            withdrawableBalance: data.data.withdrawableBalance ? toBalance(data.data.withdrawableBalance) : 0,
            ledgerBalance: data.data.ledgerBalance ? toBalance(data.data.ledgerBalance) : 0,
            savingsName: data.data.pocketName,
            accountNumber: data.data.savingsAccount,
            liquidationAccount: data.data.liquidationAccount,
            productName: data.data.productName,
            penaltyAmount: data.data.penaltyAmount,
            interestToBePaid: data.data.interestToBePaid,
            accruedInterest: data.data.accruedInterest,
            amountToWidthraw: data.data.totalAmountToWithdraw ? toBalance(data.data.totalAmountToWithdraw) : 0,
            accountStatus: data.data.status,
            id: data.data.id,
          }
          this.$root.$emit('bv::show::modal', 'liquidation-modal-lg')
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },

    handleLiquidationSubmit(evt) {
      evt.preventDefault()
      this.$refs.liquidationRules.validate().then(async success => {
        if (success) {
          const actionText = this.$t('Are you sure you want to Liquidate this Savings Pockets?')
          this.confirm(this.submitLiquidation, { text: actionText })
        }
      })
    },
    async submitLiquidation() {
      try {
        const savingsConfig = {
          FrequentSave: {
            formData: {
              frequentSavingsAccountNumber: this.liquidationFormData.accountNumber,
              liquidationAccount: this.liquidationFormData.liquidationAccount,
            },
            routePath: 'FrequentSave/Liquidate',
          },
          FlexSave: {
            formData: {
              id: this.liquidationFormData.id,
              amount: toKobo(this.liquidationFormData.amountToWidthraw),
            },
            routePath: 'FlexSavings/Withdraw',
          },
          SmartSave: {
            formData: {
              id: this.liquidationFormData.id,
            },
            routePath: 'SmartSavings/Withdraw',
          },
        }

        const { formData, routePath } = savingsConfig[this.currentSavingsType] ?? savingsConfig.SmartSave

        const response = await this.performLiquidation({
          formData,
          routePath,
          permission: accountPermissions.VIEW_SAVINGS_POCKET,
        })

        const { status, data } = response
        if (status === 200 && data?.isSuccessful) {
          this.alertSuccess({ text: this.$t('Your request to Liquidate Savings Pockets was successful') })
        } else {
          this.alertError({ text: data?.message || this.$t('Liquidation failed') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },

    handleTotalAmountToWidthraw(value) {
      if (value > this.liquidationFormData.withdrawableBalance) {
        this.alertError({ text: this.$t('Amount to withdraw exceeds withdrawable balance') })
      } else {
        this.liquidationFormData.amountToWidthraw = value
      }
    },

    accountSavingsPocketStatus(items) {
      const { id, status, savingsType } = items

      const isActive = status === 'Active'
      const action = isActive ? 'Deactivate' : 'Activate'

      const statusType = isActive ? 1 : 2

      const permission = isActive
        ? accountPermissions.DEACTIVATE_SAVINGS_POCKET
        : accountPermissions.ACTIVATE_SAVINGS_POCKET

      const routeMap = {
        FrequentSave: 'FrequentSave',
        FlexSave: 'FlexSave',
        SmartSave: 'SmartSave',
      }

      const routeName = routeMap[savingsType]

      // Validate route name
      if (!routeName) {
        this.alertError({
          text: this.$t('Invalid savings type:', { type: savingsType }),
        })
        return
      }

      const translations = {
        requestText: this.$t('Your request to'),
        success: this.$t('Savings Pocket was successful'),
        failed: this.$t('Savings Pocket failed'),
        reason: this.$t('Reason'),
        confirmQuestion: this.$t('Are you sure you want to'),
        confirmText: this.$t('this Savings Pocket account'),
      }

      const updateStatus = async () => {
        try {
          this.showSpinner = true

          const response = await this.updateSavingsPocketStatus({
            id,
            statusType,
            routeName,
            permission,
          })

          if (response?.status === 200 || response?.data?.success) {
            await this.fetchSavingsPocket()

            this.alertSuccess({
              text: `${translations.requestText} ${action} ${translations.success}`,
            })
          }
        } catch (error) {
          const errors = error.response?.data?.errors || []

          if (errors.length > 0) {
            errors.forEach(err => {
              this.alertError({
                text: `${translations.requestText} ${action} ${translations.failed}. ${translations.reason}: ${err.errorMessage || err.message || this.$t('Unknown error')}`,
              })
            })
          } else {
            this.alertError({
              text: `${translations.requestText} ${action} ${translations.failed}. ${error.message || this.$t('An unexpected error occurred')}`,
            })
          }
        } finally {
          this.showSpinner = false
        }
      }

      this.confirm(updateStatus, {
        title: this.$t('Confirm Action'),
        text: `${translations.confirmQuestion} ${action} ${translations.confirmText}?`,
      })
    },

    resetModal() {
      this.isReadonly = false
      this.isLoading = false
      this.modalTitle = 'Create Savings Pockets'
      this.buttonLabel = 'Save'
    },
    resetFilters() {
      this.filter = {
        ...null,
      }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchSavingsPocket()
    },
  },
}
</script>
